import { PlusOutlined, SettingOutlined } from '@ant-design/icons'
import { Button, Divider, Flex, Select, Typography } from 'antd'
import { Dispatch, SetStateAction, useState } from 'react'

import { MedicalAssistanceType, Permissions } from 'src/api/requests'
import { RoleSelect } from 'src/components'
import { useUser } from 'src/hooks'
import { useLocales } from 'src/locales'

import { AddInstitutionModal } from '../AddInstitutionModal'
import { InstitutionInfiniteSelect } from '../InstitutionInfiniteSelect'
import { Filters } from '../OrganizationalTree'

type Props = {
  setFilters: Dispatch<SetStateAction<Filters>>
}

export const OrganizationalHeader = ({ setFilters }: Props) => {
  const { t } = useLocales()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [isFiltersOpen, setIsFiltersOpen] = useState(false)

  const { hasPermission } = useUser()

  const onClear = (name: string) => {
    setFilters((prev) => ({ ...prev, [name]: undefined }))
  }

  const onToggleFilters = () => setIsFiltersOpen((prev) => !prev)

  const booleanOptions = [
    {
      label: t('common.yes'),
      value: true
    },
    {
      label: t('common.no'),
      value: false
    }
  ]

  const medicalAssistance = Object.values(MedicalAssistanceType)

  const medicalAssistanceTypesOptions = medicalAssistance.map((item) => ({
    label: t(`medical_assistance.${item}`),
    value: item
  }))

  return (
    <Flex vertical>
      <Flex justify="space-between" align="flex-end" wrap gap={16}>
        {hasPermission(
          Permissions.ORGANIZATIONAL_STRUCTURE_ORGANIZATIONAL_STRUCTURE_CREATE_ALL
        ) && (
          <Button type="primary" onClick={() => setIsModalOpen(true)}>
            <PlusOutlined /> {t('organizational_structure.add_button')}
          </Button>
        )}

        <Button icon={<SettingOutlined />} onClick={onToggleFilters}>
          {t('common.filters')}
        </Button>
      </Flex>

      {isFiltersOpen && (
        <>
          <Divider />

          <Flex gap={16} wrap>
            <Flex vertical gap={4}>
              <Typography.Text>{t('users.select_institution')}</Typography.Text>
              <InstitutionInfiniteSelect
                onClear={() => onClear('parentId')}
                onSelect={(value: string) =>
                  setFilters((prev) => ({
                    ...prev,
                    parentId: value
                  }))
                }
              />
            </Flex>

            <Flex vertical gap={4}>
              <Typography.Text>
                {t('organizational_structure.declaresCases')}
              </Typography.Text>

              <Select
                placeholder={t('organizational_structure.declaresCases')}
                allowClear
                onClear={() => onClear('declaresCases')}
                onSelect={(value: string) =>
                  setFilters((prev) => ({ ...prev, declaresCases: value }))
                }
                options={booleanOptions}
              />
            </Flex>

            <Flex vertical gap={4}>
              <Typography.Text>
                {t('organizational_structure.tracksCases')}
              </Typography.Text>

              <Select
                placeholder={t('organizational_structure.tracksCases')}
                allowClear
                onClear={() => onClear('tracksCases')}
                onSelect={(value: string) =>
                  setFilters((prev) => ({ ...prev, tracksCases: value }))
                }
                options={booleanOptions}
              />
            </Flex>

            <Flex vertical gap={4}>
              <Typography.Text>
                {t('organizational_structure.medical_assistance_types')}
              </Typography.Text>

              <Select
                placeholder={t(
                  'organizational_structure.medical_assistance_types'
                )}
                mode="multiple"
                style={{ maxWidth: 250, width: 250 }}
                allowClear
                onClear={() => onClear('medicalAssistanceTypes')}
                onChange={(value: string[]) =>
                  setFilters((prev) => ({
                    ...prev,
                    medicalAssistanceTypes: value
                  }))
                }
                options={medicalAssistanceTypesOptions}
              />
            </Flex>

            <Flex vertical gap={4}>
              <Typography.Text>
                {t('organizational_structure.roles')}
              </Typography.Text>

              <RoleSelect
                mode="multiple"
                hasDefault={false}
                style={{ maxWidth: 250, width: 250 }}
                allowClear
                onClear={() => onClear('roleIds')}
                onChange={(value: string[]) =>
                  setFilters((prev) => ({ ...prev, roleIds: value }))
                }
              />
            </Flex>
          </Flex>
        </>
      )}

      {isModalOpen && (
        <AddInstitutionModal onCancel={() => setIsModalOpen(false)} />
      )}
      <Divider />
    </Flex>
  )
}
